import type { CourseRegistrationManagementTabsViewModel } from '@/shared/api/generated';
import { useGetApiV1CourseregistrationEnrollmentCourseregistrationidManagementTabs } from '@/shared/api/generated';

/**
 * Visibilidade das abas do painel de gerenciamento de matrícula, já mapeada
 * das flags granulares da API para o conjunto de abas exibido pela UI atual.
 */
export interface EnrollmentManagementTabsVisibility {
  readonly details: boolean;
  readonly documents: boolean;
  readonly financial: boolean;
  readonly scholarship: boolean;
  readonly academic: boolean;
  readonly history: boolean;
}

/**
 * Mapeia as flags granulares do backend para as abas da UI.
 * `academic` agrega as abas granulares de acesso ao curso, processo seletivo,
 * indicação de aluno, monografia e histórico de polo.
 */
function mapTabs(
  vm: CourseRegistrationManagementTabsViewModel,
): EnrollmentManagementTabsVisibility {
  return {
    details: vm.details ?? true,
    documents: true,
    financial: vm.financial ?? false,
    scholarship: vm.scholarship ?? false,
    academic: Boolean(
      vm.courseAccess ||
        vm.selectiveProcess ||
        vm.studentIndicator ||
        vm.monograph ||
        vm.poloHistory,
    ),
    history: vm.history ?? true,
  };
}

/**
 * Conjunto de abas garantido enquanto as flags carregam ou em caso de erro:
 * apenas as abas essenciais ficam visíveis, evitando "flicker" de abas
 * condicionais que sumiriam após o fetch.
 */
const FALLBACK_TABS: EnrollmentManagementTabsVisibility = {
  details: true,
  documents: true,
  financial: false,
  scholarship: false,
  academic: false,
  history: true,
};

/**
 * Hook para determinar quais abas do painel de gerenciamento de uma matrícula
 * devem ser exibidas, consumindo o endpoint de management-tabs como fonte de
 * verdade (considera perfil polo/não-polo e características da matrícula).
 *
 * @param enrollmentId - ID da matrícula (path param `courseRegistrationId`)
 * @returns Visibilidade por aba já mapeada e estado de loading/erro
 *
 * @example
 * ```tsx
 * const { tabs } = useEnrollmentManagementTabs(enrollmentId);
 * {tabs.financial && <TabMenuTrigger value="financial">...</TabMenuTrigger>}
 * ```
 */
export function useEnrollmentManagementTabs(enrollmentId: number) {
  const query =
    useGetApiV1CourseregistrationEnrollmentCourseregistrationidManagementTabs(enrollmentId);

  const tabs: EnrollmentManagementTabsVisibility = query.data ? mapTabs(query.data) : FALLBACK_TABS;

  return {
    tabs,
    isLoading: query.isLoading,
    isError: query.isError,
  } as const;
}
